import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Bookmark } from "lucide-react";
import { useSaved } from "@/lib/SavedContext";
import { useExplore } from "@/lib/ExploreContext";
import InlineSearch from "@/components/yts/InlineSearch";
import SiteTools from "@/components/yts/SiteTools";
import BrandLogo from "@/components/yts/BrandLogo";
import MenuButton from "@/components/yts/menu/MenuButton";
import MenuPanel from "@/components/yts/MenuPanel";
import { useTheme } from "@/lib/theme";
import { onTourMenuChange } from "@/lib/tour";

/** Centered editorial masthead; site navigation stays fully functional. */
export default function TopNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { saved } = useSaved();
  const savedCount = saved?.size || 0;
  const { clearFilters } = useExplore() || {};
  const { isDark } = useTheme();
  const controlTone = isDark ? "!text-white hover:!bg-white/10" : "!text-[#111] hover:!bg-black/5";
  useEffect(() => onTourMenuChange(setMenuOpen), []);

  const savedButton = (size) => (
    <Link to="/saved" aria-label={`My List${savedCount ? `, ${savedCount} saved` : ""}`} className={`relative grid ${size} place-items-center rounded-none ${controlTone}`}>
      <Bookmark className="h-[23px] w-[23px] sm:h-[27px] sm:w-[27px]" strokeWidth={1.8} />
      {savedCount > 0 && <span className="absolute right-0 top-0 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#D9232E] px-0.5 text-[10px] font-bold text-white">{savedCount > 99 ? "99+" : savedCount}</span>}
    </Link>
  );

  return (
    <>
      <header className={`sticky top-0 z-50 border-b backdrop-blur-md ${isDark ? "border-white/15 bg-[#171719] text-white" : "border-black/10 bg-white text-[#111]"}`}>
        <div className="mx-auto grid h-[60px] max-w-[1440px] grid-cols-[46px_minmax(0,1fr)_90px] items-center gap-1 px-3 sm:hidden">
          <MenuButton data-tour="menu" onClick={() => setMenuOpen(true)} className={`!h-11 !w-11 !rounded-none !border-0 !bg-transparent !shadow-none ${controlTone}`} />
          <Link to="/" onClick={() => clearFilters?.()} className="mx-auto flex w-full max-w-[174px] flex-col items-center justify-center" aria-label="Ya Tú Sabes home">
            <BrandLogo variant="header" className="!h-auto !w-full" />
          </Link>
          <div className="flex justify-self-end">
            <button type="button" data-tour="search" onClick={() => setSearchOpen((open) => !open)} aria-label="Search Ya Tú Sabes PR" aria-expanded={searchOpen} className={`grid h-11 w-11 place-items-center ${controlTone}`}>
              <Search className="h-[24px] w-[24px]" strokeWidth={1.8} />
            </button>
            {savedButton("h-11 w-11")}
          </div>
        </div>
        <nav className="mx-auto hidden h-[64px] max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center px-6 sm:grid lg:px-10">
          <div className="justify-self-start">
            <MenuButton data-tour="menu" onClick={() => setMenuOpen(true)} className={`!h-12 !w-12 !rounded-none !border-0 !bg-transparent !shadow-none ${controlTone}`} />
          </div>
          <Link to="/" onClick={() => clearFilters?.()} className="flex w-[214px] flex-col items-center justify-center lg:w-[230px]" aria-label="Ya Tú Sabes home">
            <BrandLogo variant="header" className="!h-auto !w-full" />
          </Link>
          <div className="flex items-center justify-self-end gap-1">
            <button type="button" data-tour="search" onClick={() => setSearchOpen((open) => !open)} aria-label="Toggle search" aria-expanded={searchOpen} className={`grid h-12 w-12 place-items-center ${controlTone}`}><Search className="h-7 w-7" strokeWidth={1.8} /></button>
            {savedButton("h-12 w-12")}
            <SiteTools className={`ml-1 shrink-0 ${isDark ? "!border-white/15 !bg-white/5 !text-white" : "!border-black/10 !bg-white !text-[#111]"}`} />
          </div>
        </nav>
        {searchOpen && <div className="mx-auto max-w-[900px] px-4 pb-3 sm:px-6"><InlineSearch variant="bar" /></div>}
      </header>
      <MenuPanel open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );
}
